import React, { useState, useEffect, useCallback } from 'react';
import { formatTime12, toViewerZone, viewerTz } from '../utils/timeZone';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock,
  User,
  MapPin,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Users,
  AlertCircle,
  CheckCircle,
  FileText,
  Briefcase,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { CalendarAppointmentActions } from '../components/CalendarAppointmentActions';
import { AppointmentEditDialog } from '../components/AppointmentEditDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Event type configurations with colors
const EVENT_TYPES = {
  appointment: { label: 'Appointment', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  deadline: { label: 'Deadline', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
  follow_up: { label: 'Follow-up', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
  staff_schedule: { label: 'Staff Schedule', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' }
};

const SCHEDULE_TYPES = [
  { id: 'available', label: 'Available', color: 'bg-green-500' },
  { id: 'busy', label: 'Busy', color: 'bg-gray-500' },
  { id: 'pto', label: 'PTO/Leave', color: 'bg-purple-500' },
  { id: 'training', label: 'Training', color: 'bg-indigo-500' },
  { id: 'meeting', label: 'Meeting', color: 'bg-teal-500' }
];

// Time slots for week view (6 AM to 8 PM)
const TIME_SLOTS = Array.from({ length: 15 }, (_, i) => {
  const hour = i + 6;
  return {
    hour,
    label: hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`,
    time: `${String(hour).padStart(2, '0')}:00`
  };
});

const ManagementCalendarPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // week, month, day
  const [showFilters, setShowFilters] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Filter state
  const [staffList, setStaffList] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [filters, setFilters] = useState({
    staff_id: '_all',
    program_type: '_all',
    location: '',
    event_types: ['appointments', 'deadlines', 'staff_schedules']
  });

  // New schedule form
  const [scheduleForm, setScheduleForm] = useState({
    staff_id: '',
    staff_name: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    schedule_type: 'available',
    notes: ''
  });

  // Check access
  const hasAccess = ['system_admin', 'executive', 'operations_manager'].includes(user?.role);

  useEffect(() => {
    if (hasAccess) {
      fetchStaffList();
      fetchProgramTypes();
    }
  }, [hasAccess]);

  useEffect(() => {
    if (hasAccess) {
      fetchEvents();
    }
  }, [currentDate, viewMode, filters, hasAccess]);

  // Keep the calendar current: quiet refresh every 30s and when the tab regains focus
  useEffect(() => {
    if (!hasAccess) return undefined;
    const t = setInterval(() => fetchEvents(true), 30000);
    const onFocus = () => fetchEvents(true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [currentDate, viewMode, filters, hasAccess]);

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/management-calendar/staff`);
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const fetchProgramTypes = async () => {
    try {
      const response = await axios.get(`${API}/enrollment-types`);
      setProgramTypes(response.data.enrollment_types || []);
    } catch (error) {
      console.error('Failed to fetch program types:', error);
    }
  };

  const getDateRange = useCallback(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
    } else if (viewMode === 'month') {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return { start: format(start, 'yyyy-MM-dd'), end: format(end, 'yyyy-MM-dd') };
    } else {
      // day view
      return { start: format(currentDate, 'yyyy-MM-dd'), end: format(currentDate, 'yyyy-MM-dd') };
    }
  }, [currentDate, viewMode]);

  const fetchEvents = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = new URLSearchParams({
        start_date: start,
        end_date: end,
        event_types: filters.event_types.join(',')
      });
      
      if (filters.staff_id && filters.staff_id !== '_all') {
        params.append('staff_id', filters.staff_id);
      }
      if (filters.program_type && filters.program_type !== '_all') {
        params.append('program_type', filters.program_type);
      }
      if (filters.location) {
        params.append('location', filters.location);
      }

      const response = await axios.get(`${API}/management-calendar/events?${params}`);
      setEvents((response.data.events || []).map(e => toViewerZone(e, viewerTz(user))));
    } catch (error) {
      if (silent) return;
      if (error.response?.status === 403) {
        toast.error('Access denied. Management calendar requires Operations Manager role or above.');
      } else {
        toast.error('Failed to load calendar events');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (viewMode === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else if (viewMode === 'month') {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? addDays(currentDate, -1) : addDays(currentDate, 1));
    }
  };

  const handleCreateSchedule = async () => {
    if (!scheduleForm.staff_id || !scheduleForm.date) {
      toast.error('Please select staff member and date');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/management-calendar/staff-schedule`, scheduleForm);
      toast.success('Staff schedule saved');
      setShowScheduleDialog(false);
      setScheduleForm({
        staff_id: '',
        staff_name: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '17:00',
        schedule_type: 'available',
        notes: ''
      });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventDialog(true);
  };

  const toggleEventType = (type) => {
    setFilters(prev => ({
      ...prev,
      event_types: prev.event_types.includes(type)
        ? prev.event_types.filter(t => t !== type)
        : [...prev.event_types, type]
    }));
  };

  // Get week days for header
  const getWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  // Get events for a specific day and time
  const getEventsForSlot = (date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      if (event.date !== dateStr) return false;
      if (event.all_day) return hour === 6; // Show all-day events at 6 AM
      const eventHour = parseInt(event.start_time?.split(':')[0] || '0');
      return eventHour === hour;
    });
  };

  // Get events for a specific day (month view)
  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => event.date === dateStr);
  };

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              The Management Calendar is only available to Operations Managers and above.
            </p>
            <Button asChild className="mt-4">
              <Link to="/appointments">Go to My Appointments</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const weekDays = getWeekDays();

  return (
    <div className="space-y-4" data-testid="management-calendar-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-primary" />
            Management Calendar
          </h1>
          <p className="text-muted-foreground">
            Centralized view of appointments, deadlines, and staff schedules
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} data-testid="filter-btn">
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {(filters.staff_id !== '_all' || filters.program_type !== '_all' || filters.location) && (
              <Badge variant="secondary" className="ml-2">Active</Badge>
            )}
          </Button>
          <Button variant="outline" onClick={() => setShowScheduleDialog(true)} data-testid="add-schedule-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Staff Schedule
          </Button>
          <Button variant="outline" onClick={() => fetchEvents()} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Staff Member</Label>
                <Select value={filters.staff_id} onValueChange={(v) => setFilters(prev => ({ ...prev, staff_id: v }))}>
                  <SelectTrigger data-testid="filter-staff">
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Staff</SelectItem>
                    {staffList.map(staff => (
                      <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Program</Label>
                <Select value={filters.program_type} onValueChange={(v) => setFilters(prev => ({ ...prev, program_type: v }))}>
                  <SelectTrigger data-testid="filter-program">
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Programs</SelectItem>
                    {programTypes.map(pt => (
                      <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  placeholder="Filter by location..."
                  value={filters.location}
                  onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                  data-testid="filter-location"
                />
              </div>
              <div className="space-y-2">
                <Label>Event Types</Label>
                <div className="flex flex-wrap gap-2 pt-1">
                  {Object.entries(EVENT_TYPES).map(([key, config]) => (
                    <Badge
                      key={key}
                      variant={filters.event_types.includes(key === 'staff_schedule' ? 'staff_schedules' : key + 's') ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleEventType(key === 'staff_schedule' ? 'staff_schedules' : key + 's')}
                    >
                      <span className={`w-2 h-2 rounded-full ${config.color} mr-1`} />
                      {config.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Controls */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => handleNavigate('prev')} data-testid="nav-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => handleNavigate('next')} data-testid="nav-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setCurrentDate(new Date())} data-testid="nav-today">
                Today
              </Button>
              <h2 className="text-lg font-semibold ml-2">
                {viewMode === 'week' && `${format(weekDays[0], 'MMM d')} - ${format(weekDays[6], 'MMM d, yyyy')}`}
                {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
                {viewMode === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
              </h2>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant={viewMode === 'day' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('day')}
              >
                Day
              </Button>
              <Button 
                variant={viewMode === 'week' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
              <Button 
                variant={viewMode === 'month' ? 'default' : 'outline'} 
                size="sm"
                onClick={() => setViewMode('month')}
              >
                Month
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : viewMode === 'week' ? (
            /* Week View */
            <div className="border rounded-lg overflow-hidden">
              {/* Day Headers */}
              <div className="grid grid-cols-8 border-b bg-muted/50">
                <div className="p-2 text-xs font-medium text-muted-foreground border-r">Time</div>
                {weekDays.map((day, i) => (
                  <div 
                    key={i} 
                    className={`p-2 text-center border-r last:border-r-0 ${isSameDay(day, new Date()) ? 'bg-primary/10' : ''}`}
                  >
                    <div className="text-xs text-muted-foreground">{format(day, 'EEE')}</div>
                    <div className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>
                      {format(day, 'd')}
                    </div>
                  </div>
                ))}
              </div>
              {/* Time Slots */}
              <div className="max-h-[600px] overflow-y-auto">
                {TIME_SLOTS.map((slot) => (
                  <div key={slot.hour} className="grid grid-cols-8 border-b last:border-b-0 min-h-[60px]">
                    <div className="p-1 text-xs text-muted-foreground border-r flex items-start justify-end pr-2">
                      {slot.label}
                    </div>
                    {weekDays.map((day, i) => {
                      const slotEvents = getEventsForSlot(day, slot.hour);
                      return (
                        <div 
                          key={i} 
                          className={`p-1 border-r last:border-r-0 min-h-[60px] ${isSameDay(day, new Date()) ? 'bg-primary/5' : ''}`}
                        >
                          {slotEvents.map(event => (
                            <div
                              key={event.id}
                              onClick={() => handleEventClick(event)}
                              data-testid={`calendar-event-${event.id}`}
                              className={`text-xs p-1 rounded mb-1 cursor-pointer truncate ${EVENT_TYPES[event.event_type]?.bgLight || 'bg-gray-50'} ${EVENT_TYPES[event.event_type]?.textColor || 'text-gray-700'} hover:opacity-80`}
                              title={event.title}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${EVENT_TYPES[event.event_type]?.color || 'bg-gray-500'}`} />
                              {event.title}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          ) : viewMode === 'month' ? (
            /* Month View */
            <div className="border rounded-lg">
              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b bg-muted/50">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground border-r last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {Array.from({ length: 42 }, (_, i) => {
                  const monthStart = startOfMonth(currentDate);
                  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
                  const day = addDays(calendarStart, i);
                  const dayEvents = getEventsForDay(day);
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  const isToday = isSameDay(day, new Date());
                  
                  return (
                    <div 
                      key={i} 
                      className={`min-h-[80px] p-1 border-r border-b ${!isCurrentMonth ? 'bg-muted/30' : ''} ${isToday ? 'bg-primary/5' : ''}`}
                    >
                      <div className={`text-sm font-medium mb-1 ${!isCurrentMonth ? 'text-muted-foreground' : ''} ${isToday ? 'text-primary' : ''}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map(event => (
                          <div
                            key={event.id}
                            onClick={() => handleEventClick(event)}
                            data-testid={`calendar-event-${event.id}`}
                            className={`text-xs p-0.5 rounded truncate cursor-pointer ${EVENT_TYPES[event.event_type]?.bgLight || 'bg-gray-50'} ${EVENT_TYPES[event.event_type]?.textColor || 'text-gray-700'} hover:opacity-80`}
                          >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-0.5 ${EVENT_TYPES[event.event_type]?.color || 'bg-gray-500'}`} />
                            {event.title.substring(0, 15)}...
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-muted-foreground pl-1">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Day View */
            <div className="border rounded-lg">
              <div className="max-h-[600px] overflow-y-auto">
                {TIME_SLOTS.map((slot) => {
                  const slotEvents = getEventsForSlot(currentDate, slot.hour);
                  return (
                    <div key={slot.hour} className="flex border-b last:border-b-0 min-h-[60px]">
                      <div className="w-20 p-2 text-sm text-muted-foreground border-r flex items-start justify-end pr-3">
                        {slot.label}
                      </div>
                      <div className="flex-1 p-2">
                        {slotEvents.map(event => (
                          <div
                            key={event.id}
                            onClick={() => handleEventClick(event)}
                            data-testid={`calendar-event-${event.id}`}
                            className={`p-2 rounded mb-1 cursor-pointer ${EVENT_TYPES[event.event_type]?.bgLight || 'bg-gray-50'} ${EVENT_TYPES[event.event_type]?.textColor || 'text-gray-700'} hover:opacity-80`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${EVENT_TYPES[event.event_type]?.color || 'bg-gray-500'}`} />
                              <span className="font-medium">{event.title}</span>
                              <span className="text-xs text-muted-foreground">
                                {formatTime12(event.start_time)} - {formatTime12(event.end_time)}
                              </span>
                            </div>
                            {event.participant_name && (
                              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {event.participant_name}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <span className="text-sm text-muted-foreground">Legend:</span>
            {Object.entries(EVENT_TYPES).map(([key, config]) => (
              <div key={key} className="flex items-center gap-1 text-sm">
                <span className={`w-3 h-3 rounded-full ${config.color}`} />
                {config.label}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Event Details Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-md" data-testid="calendar-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && (
                <>
                  <span className={`w-3 h-3 rounded-full ${EVENT_TYPES[selectedEvent.event_type]?.color}`} />
                  {EVENT_TYPES[selectedEvent.event_type]?.label}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{selectedEvent.title}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                  <span>{format(parseISO(selectedEvent.date), 'EEEE, MMMM d, yyyy')}</span>
                </div>
                {!selectedEvent.all_day && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>{formatTime12(selectedEvent.start_time)} - {formatTime12(selectedEvent.end_time)}</span>
                  </div>
                )}
                {selectedEvent.participant_name && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedEvent.participant_name}</span>
                  </div>
                )}
                {selectedEvent.staff_name && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedEvent.staff_name}</span>
                  </div>
                )}
                {selectedEvent.location && (
                  <div className="flex items-center gap-2 col-span-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedEvent.location}</span>
                  </div>
                )}
                {selectedEvent.program_name && (
                  <div className="flex items-center gap-2 col-span-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedEvent.program_name}</span>
                  </div>
                )}
              </div>
              {selectedEvent.notes && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground">{selectedEvent.notes}</p>
                </div>
              )}
              {selectedEvent.event_type === 'appointment' && selectedEvent.participant_id && (
                <div className="pt-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/participants/${selectedEvent.participant_id}`}>
                      View Participant
                    </Link>
                  </Button>
                </div>
              )}
              <CalendarAppointmentActions
                event={selectedEvent}
                role={user?.role}
                onEdit={(ev) => { setShowEventDialog(false); setEditingAppointment(ev); }}
                onDeleted={() => { setShowEventDialog(false); setSelectedEvent(null); fetchEvents(true); }}
              />
              {(selectedEvent.event_type === 'deadline' || selectedEvent.event_type === 'follow_up') && selectedEvent.case_id && (
                <div className="pt-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/cases/${selectedEvent.case_id}`}>
                      View Case
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AppointmentEditDialog
        appointment={editingAppointment}
        onClose={() => setEditingAppointment(null)}
        onSaved={() => { setEditingAppointment(null); fetchEvents(true); }}
      />

      {/* Add Staff Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Schedule</DialogTitle>
            <DialogDescription>
              Set availability or schedule for a staff member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Staff Member *</Label>
              <Select 
                value={scheduleForm.staff_id}
                onValueChange={(v) => {
                  const staff = staffList.find(s => s.id === v);
                  setScheduleForm(prev => ({ 
                    ...prev, 
                    staff_id: v, 
                    staff_name: staff?.name || '' 
                  }));
                }}
              >
                <SelectTrigger data-testid="schedule-staff">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.map(staff => (
                    <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={scheduleForm.date}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, date: e.target.value }))}
                data-testid="schedule-date"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.start_time}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, start_time: e.target.value }))}
                  data-testid="schedule-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.end_time}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, end_time: e.target.value }))}
                  data-testid="schedule-end"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Schedule Type</Label>
              <Select 
                value={scheduleForm.schedule_type}
                onValueChange={(v) => setScheduleForm(prev => ({ ...prev, schedule_type: v }))}
              >
                <SelectTrigger data-testid="schedule-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHEDULE_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Optional notes..."
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="schedule-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSchedule} disabled={saving} data-testid="save-schedule-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagementCalendarPage;
